import { skipToken } from '@reduxjs/toolkit/query';
import { ValidationProblemCode } from '@ydistri/api-sdk';
import { Text } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { Checkbox, Form, Input, Modal } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  AI_PROMPT_CONTENT_MAX_LENGTH,
  AI_PROMPT_TITLE_MAX_LENGTH,
} from '../../components/global/_constants';
import { useUser } from '../../hooks/useUser';
import { ReduxState, useAppDispatch } from '../../store';
import {
  fieldErrors,
  messageFor,
  problemMessagesOf,
  problemsOfError,
} from '../Configuration/Validator/problemsLib';
import {
  useCreateAiPromptMutation,
  useGetAiPromptQuery,
  useSaveAiPromptMutation,
} from '../ProjectAdministration/AiPrompts/apiAiPrompts';
import { AvailableModals, closeModal } from './modalSlice';

const modalId = AvailableModals.AI_PROMPT_EDIT;

interface FormFields {
  title: string;
  content: string;
  isSuperUserOnly: boolean;
}

const fieldOfPath: Record<string, keyof FormFields> = {
  title: 'title',
  content: 'content',
  isSuperUserOnly: 'isSuperUserOnly',
};

const titleRules = [
  {
    required: true,
    whitespace: true,
    message: messageFor(ValidationProblemCode.AiPromptTitleRequired),
  },
];
const contentRules = [
  {
    required: true,
    whitespace: true,
    message: messageFor(ValidationProblemCode.AiPromptContentRequired),
  },
];
const initialValues: FormFields = { title: '', content: '', isSuperUserOnly: false };

const AiPromptModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const aiPrompt = useSelector((state: ReduxState) => state.modal.modalData[modalId]?.aiPrompt);
  const isSuperUser = useUser()?.isSuperUser ?? false;
  const [form] = Form.useForm<FormFields>();

  const { data: current, isError } = useGetAiPromptQuery(aiPrompt?.aiPromptId ?? skipToken);
  const [createAiPrompt, { isLoading: creating }] = useCreateAiPromptMutation();
  const [saveAiPrompt, { isLoading: saving }] = useSaveAiPromptMutation();

  // The flag as this session loaded it; the fields are filled once, so a refetch never replaces a draft.
  const loadedFlag = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const version = current?.versions.at(0);
    if (current && version && loadedFlag.current === undefined) {
      loadedFlag.current = current.isSuperUserOnly ?? false;
      form.setFieldsValue({
        title: version.title,
        content: version.content,
        isSuperUserOnly: current.isSuperUserOnly ?? false,
      });
    }
  }, [current, form]);

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then(values => {
        // The flag is sent only when a superadmin changed it here; the server keeps it otherwise.
        const isSuperUserOnly =
          isSuperUser && values.isSuperUserOnly !== (loadedFlag.current ?? false)
            ? values.isSuperUserOnly
            : undefined;
        const write = aiPrompt
          ? saveAiPrompt({
              aiPromptId: aiPrompt.aiPromptId,
              payload: { title: values.title, content: values.content, isSuperUserOnly },
            }).unwrap()
          : createAiPrompt({
              title: values.title,
              content: values.content,
              isSuperUserOnly,
            }).unwrap();

        write.then(handleClose).catch((error: unknown) => {
          const { fields } = fieldErrors(problemsOfError(error), fieldOfPath);
          if (fields.length > 0) {
            form.setFields(fields);
          } else {
            dispatch(
              addToast({
                message: problemMessagesOf(error, 'The prompt was not saved'),
                isError: true,
              }),
            );
          }
        });
      })
      .catch(() => {
        dispatch(addToast({ message: 'Please check required fields', isError: true }));
      });
  }, [aiPrompt, createAiPrompt, dispatch, form, handleClose, isSuperUser, saveAiPrompt]);

  return (
    <Modal
      data-type="modal"
      title={aiPrompt ? 'Edit prompt' : 'New prompt'}
      open={true}
      width="80%"
      okText="Save"
      cancelText="Cancel"
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={creating || saving}
      okButtonProps={{ disabled: aiPrompt !== undefined && current === undefined }}
    >
      {aiPrompt && isError && <Text $type="danger">The prompt could not be loaded.</Text>}
      <Form form={form} name="ai_prompt_modal" layout="vertical" initialValues={initialValues}>
        <Form.Item name="title" label="Title" rules={titleRules}>
          <Input maxLength={AI_PROMPT_TITLE_MAX_LENGTH} />
        </Form.Item>
        <Form.Item name="content" label="Content" rules={contentRules}>
          <Input.TextArea
            maxLength={AI_PROMPT_CONTENT_MAX_LENGTH}
            autoSize={{ minRows: 12, maxRows: 30 }}
          />
        </Form.Item>
        {isSuperUser && (
          <Form.Item name="isSuperUserOnly" valuePropName="checked">
            <Checkbox>Superadmin only</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AiPromptModal;
